import type { BasketRenderState, BasketState } from './basket';
import type { Camera } from './camera';
import { canvasHeight, canvasWidth, initialZoom, Themes, winnerAreaHeight } from './data/constants';
import { SPRITE_EXTENT } from './data/fruits';
import type { StageDef } from './data/maps';
import { getFruitSprite } from './fruitSprites';
import type { GameObject } from './gameObject';
import type { Marble } from './marble';
import { MINIMAP_INSET, MINIMAP_WIDTH } from './minimap';
import type { WinnerRange } from './options';
import type { ParticleManager } from './particleManager';
import type { Rect } from './types/rect.type';
import type { ColorTheme } from './types/ColorTheme';
import type { MapEntityState } from './types/MapEntity.type';
import type { VectorLike } from './types/VectorLike';
import type { UIObject } from './UIObject';
import { closeButtonSize, drawCloseCircle } from './utils/canvasCloseButton';

export type RenderParameters = {
  camera: Camera;
  stage: StageDef;
  entities: MapEntityState[];
  marbles: Marble[];
  winners: Marble[];
  particleManager: ParticleManager;
  effects: GameObject[];
  winnerRange: WinnerRange;
  /** 진행 중에는 null, 당첨자가 모두 확정되면 당첨자 배열 */
  result: Marble[] | null;
  /** 바구니 모드일 때만 채워진다. 이 값의 유무가 곧 현재 모드다 */
  baskets: BasketRenderState | null;
  size: VectorLike;
  theme: ColorTheme;
};

const MAX_DISPLAY_WIDTH = 1920;
const WINNER_TEXT_OFFSET = 30;
const RESULT_PANEL_MAX_WIDTH_RATIO = 0.9;
const RESULT_PANEL_MAX_HEIGHT_RATIO = 0.8;
const RESULT_COLUMN_MAX_WIDTH = 280;
/**
 * 팀 결과 팝업이 쓸 수 있는 세로 비율. 나머지는 아래쪽 세팅 패널 몫으로 남긴다.
 * 순위 모드 팝업(RESULT_PANEL_MAX_HEIGHT_RATIO)과 따로 두는 이유는, 팀 결과는 팀 수가
 * 많아지면 칸이 좁아져 한 줄에 한 명씩만 들어가고 그만큼 세로로 길어지기 때문이다
 */
const RESULT_BASKET_MAX_HEIGHT_RATIO = 0.62;
/** 팀 결과에서 한 팀 칸에 이름을 몇 개씩 늘어놓을지. 칸이 좁으면 이보다 적게 들어간다 */
const RESULT_MEMBERS_PER_ROW = 3;
/** 그래도 세로로 넘칠 때만 여기까지 늘린다 */
const RESULT_MEMBERS_PER_ROW_MAX = 8;
/**
 * 이름 줄 높이(기준 줄 높이의 배수). 글자 크기가 여기서 파생되므로 이 값을 올리면
 * 글자가 커지고 줄 간격도 함께 넓어진다
 */
const RESULT_MEMBER_LINE_SCALE = 1.25;
/** 줄 높이 대비 글자 크기 */
const RESULT_MEMBER_FONT_RATIO = 0.78;
const RESULT_MEMBER_MIN_LINE_HEIGHT = 10;
const PROGRESS_MAX_WIDTH_RATIO = 0.3;
const PROGRESS_ACCENT = 'rgba(255, 215, 0, 0.8)';

function inRect(rect: Rect | undefined, x: number, y: number): boolean {
  return !!rect && x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h;
}

export class RouletteRenderer {
  protected _canvas!: HTMLCanvasElement;
  protected _sceneCanvas!: HTMLCanvasElement;
  protected ctx!: CanvasRenderingContext2D;
  private _displayCtx!: CanvasRenderingContext2D;
  public sizeFactor = 1;

  protected _images: { [key: string]: HTMLImageElement } = {};
  protected _theme: ColorTheme = Themes.dark;
  private _resultCloseRect: Rect | null = null;
  private _resultPopupClosed = false;
  private _lastResult: Marble[] | null = null;
  private _lastBasketFinished = false;
  /** 팀 결과 팝업의 아래 끝(뷰포트 px). 팝업이 닫혀 있으면 -1 */
  private _resultBottomPx = -1;
  get width() {
    return this._sceneCanvas.width;
  }

  get height() {
    return this._sceneCanvas.height;
  }

  get canvas() {
    return this._canvas;
  }

  set theme(value: ColorTheme) {
    this._theme = value;
  }

  async init() {
    await this._load();

    this._canvas = document.createElement('canvas');
    this._canvas.width = canvasWidth;
    this._canvas.height = canvasHeight;
    this._displayCtx = this._canvas.getContext('2d', {
      alpha: false,
    }) as CanvasRenderingContext2D;

    this._sceneCanvas = document.createElement('canvas');
    this._sceneCanvas.width = canvasWidth;
    this._sceneCanvas.height = canvasHeight;
    this.ctx = this._sceneCanvas.getContext('2d', {
      alpha: false,
    }) as CanvasRenderingContext2D;

    document.body.appendChild(this._canvas);

    const resizing = (entries?: ResizeObserverEntry[]) => {
      const realSize = entries ? entries[0].contentRect : this._canvas.getBoundingClientRect();
      if (realSize.width <= 0 || realSize.height <= 0) return;

      const width = Math.max(realSize.width / 2, 640);
      const height = (width / realSize.width) * realSize.height;
      this._sceneCanvas.width = width;
      this._sceneCanvas.height = height;
      this.sizeFactor = width / realSize.width;

      const displayWidth = Math.min(realSize.width, MAX_DISPLAY_WIDTH);
      this._canvas.width = displayWidth;
      this._canvas.height = (displayWidth / realSize.width) * realSize.height;
    };

    const resizeObserver = new ResizeObserver(resizing);

    resizeObserver.observe(this._canvas);
    resizing();
  }

  private async _loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((rs) => {
      const img = new Image();
      img.addEventListener('load', () => {
        rs(img);
      });
      img.src = url;
    });
  }

  private async _load(): Promise<void> {
    const loadPromises = [
      { name: '챔루', imgUrl: new URL('../assets/images/chamru.png', import.meta.url) },
      { name: '쿠빈', imgUrl: new URL('../assets/images/kubin.png', import.meta.url) },
      { name: '꽉변', imgUrl: new URL('../assets/images/kkwak.png', import.meta.url) },
      { name: '꽉변호사', imgUrl: new URL('../assets/images/kkwak.png', import.meta.url) },
      { name: '꽉 변호사', imgUrl: new URL('../assets/images/kkwak.png', import.meta.url) },
      { name: '주누피', imgUrl: new URL('../assets/images/junyoop.png', import.meta.url) },
      { name: '왈도쿤', imgUrl: new URL('../assets/images/waldokun.png', import.meta.url) },
    ].map(({ name, imgUrl }) => {
      return (async () => {
        this._images[name] = await this._loadImage(imgUrl.toString());
      })();
    });

    loadPromises.push(
      (async () => {
        await this._loadImage(new URL('../assets/images/ff.svg', import.meta.url).toString());
      })()
    );

    await Promise.all(loadPromises);
  }

  private getMarbleImage(name: string): CanvasImageSource | undefined {
    return this._images[name];
  }

  protected onBeforeEntities(): void {}
  protected onAfterScene(): void {}

  render(renderParameters: RenderParameters, uiObjects: UIObject[]) {
    this._theme = renderParameters.theme;
    this.ctx.fillStyle = this._theme.background;
    this.ctx.fillRect(0, 0, this._sceneCanvas.width, this._sceneCanvas.height);

    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    this.ctx.font = '0.4pt sans-serif';
    this.ctx.lineWidth = 3 / (renderParameters.camera.zoom + initialZoom);
    renderParameters.camera.renderScene(this.ctx, () => {
      this.onBeforeEntities();
      // 칸 배경은 지형 아래에, 이름과 명단은 구슬 위에 그린다
      this.renderBasketCells(renderParameters);
      this.renderEntities(renderParameters.entities);
      this.renderEffects(renderParameters);
      this.renderMarbles(renderParameters);
      this.renderBasketContents(renderParameters);
    });
    this.ctx.restore();
    this.onAfterScene();

    uiObjects.forEach((obj) =>
      obj.render(this.ctx, renderParameters, this._sceneCanvas.width, this._sceneCanvas.height)
    );
    renderParameters.particleManager.render(this.ctx);
    this.renderWinnerProgress(renderParameters);
    this.renderResult(renderParameters);
    this.renderBasketResult(renderParameters);

    this._displayCtx.drawImage(this._sceneCanvas, 0, 0, this._canvas.width, this._canvas.height);
  }

  private renderEntities(entities: MapEntityState[]) {
    this.ctx.save();
    entities.forEach((entity) => {
      const transform = this.ctx.getTransform();
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      this.ctx.fillStyle = entity.shape.color ?? this._theme.entity[entity.shape.type].fill;
      this.ctx.strokeStyle = entity.shape.color ?? this._theme.entity[entity.shape.type].outline;
      this.ctx.shadowBlur = this._theme.entity[entity.shape.type].bloomRadius;
      this.ctx.shadowColor =
        entity.shape.bloomColor ?? entity.shape.color ?? this._theme.entity[entity.shape.type].bloom;
      const shape = entity.shape;
      switch (shape.type) {
        case 'polyline':
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case 'box': {
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        }
        case 'circle':
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }

      this.ctx.setTransform(transform);
    });
    this.ctx.restore();
  }

  /** 바구니 index 로 칸을 구분하는 색. 구슬 색과 겹치지 않게 채도를 낮춰 쓴다 */
  private static basketHue(index: number, count: number): number {
    return (360 / Math.max(1, count)) * index;
  }

  /** 칸 배경. 정원이 찬 칸은 더 진하게 칠해 닫혔음을 알린다 */
  private renderBasketCells({ baskets }: RenderParameters) {
    const state = baskets;
    if (!state) return;

    const ctx = this.ctx;
    const count = state.baskets.length;
    const height = state.floorY - state.mouthY;

    ctx.save();
    ctx.shadowBlur = 0;
    for (const basket of state.baskets) {
      const isFull = basket.members.length >= basket.quota;
      ctx.fillStyle = `hsl(${RouletteRenderer.basketHue(basket.index, count)} 70% 50% / ${isFull ? 0.4 : 0.18})`;
      ctx.fillRect(basket.left, state.mouthY, basket.right - basket.left, height);
    }
    ctx.restore();
  }

  /**
   * 칸 이름과 담긴 명단. 월드 좌표에 자리를 잡고 1/zoom 으로 되돌려서 화면 픽셀 단위로 그린다
   * (Marble 의 이름 표시와 같은 방식)
   */
  private renderBasketContents({ baskets, camera, theme }: RenderParameters) {
    const state = baskets;
    if (!state) return;

    const ctx = this.ctx;
    const zoom = camera.zoom * initialZoom;
    const count = state.baskets.length;
    const boxHeight = (state.floorY - state.mouthY) * zoom;

    for (const basket of state.baskets) {
      const boxWidth = (basket.right - basket.left) * zoom;
      const titleSize = Math.min(22, Math.max(9, boxWidth * 0.18));
      const lineHeight = Math.min(15, Math.max(8, titleSize * 0.72));
      const pad = Math.max(2, titleSize * 0.2);
      const headerHeight = titleSize + lineHeight * 0.9 + pad;

      ctx.save();
      ctx.translate((basket.left + basket.right) / 2, state.mouthY);
      ctx.scale(1 / zoom, 1 / zoom);
      // 여기서부터 1단위 = 화면 1픽셀. 칸을 넘는 글자는 잘라낸다
      ctx.beginPath();
      ctx.rect(-boxWidth / 2, 0, boxWidth, boxHeight);
      ctx.clip();

      ctx.shadowBlur = 0;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;

      ctx.font = `bold ${titleSize}px sans-serif`;
      ctx.fillStyle = `hsl(${RouletteRenderer.basketHue(basket.index, count)} 90% ${theme.marbleLightness}%)`;
      ctx.strokeText(basket.name, 0, pad);
      ctx.fillText(basket.name, 0, pad);

      ctx.font = `${lineHeight * 0.9}px sans-serif`;
      ctx.fillStyle = theme.winnerText;
      const counter = `${basket.members.length} / ${basket.quota}`;
      ctx.strokeText(counter, 0, pad + titleSize);
      ctx.fillText(counter, 0, pad + titleSize);

      this.renderBasketMembers(basket, boxHeight - headerHeight - pad, headerHeight, lineHeight, theme);
      ctx.restore();
    }
  }

  /** 화면 픽셀 좌표로 호출된다. 칸 높이에 안 들어가는 만큼은 마지막 줄에 접어 표시한다 */
  private renderBasketMembers(
    basket: BasketState,
    availableHeight: number,
    top: number,
    lineHeight: number,
    theme: ColorTheme
  ) {
    const ctx = this.ctx;
    const capacity = Math.floor(availableHeight / lineHeight);
    if (capacity < 1 || basket.members.length === 0) return;

    const hidden = basket.members.length > capacity ? basket.members.length - capacity + 1 : 0;
    const shown = hidden > 0 ? basket.members.slice(0, capacity - 1) : basket.members;

    ctx.font = `${lineHeight * 0.85}px sans-serif`;
    ctx.lineWidth = 1.5;
    shown.forEach((member, i) => {
      ctx.fillStyle = `hsl(${member.hue} 100% ${theme.marbleLightness}%)`;
      ctx.strokeText(member.name, 0, top + i * lineHeight);
      ctx.fillText(member.name, 0, top + i * lineHeight);
    });

    if (hidden > 0) {
      ctx.fillStyle = theme.winnerText;
      ctx.fillText(`+${hidden}`, 0, top + shown.length * lineHeight);
    }
  }

  private renderEffects({ effects, camera }: RenderParameters) {
    effects.forEach((effect) => effect.render(this.ctx, camera.zoom * initialZoom, this._theme));
  }

  private renderMarbles({ marbles, camera, winnerRange, winners, size }: RenderParameters) {
    const firstIndex = winnerRange.start - winners.length;
    const lastIndex = winnerRange.end - winners.length;

    const viewPort = { x: camera.x, y: camera.y, w: size.x, h: size.y, zoom: camera.zoom * initialZoom };
    marbles.forEach((marble, i) => {
      marble.render(
        this.ctx,
        camera.zoom * initialZoom,
        i >= firstIndex && i <= lastIndex,
        false,
        this.getMarbleImage(marble.name),
        viewPort,
        this._theme
      );
    });
  }

  private renderResult(params: RenderParameters) {
    const result = params.result;
    // 새 결과가 나오면(또는 리셋되면) 닫힘 상태를 푼다. _result는 확정될 때마다 새 배열이다
    if (result !== this._lastResult) {
      this._lastResult = result;
      this._resultPopupClosed = false;
    }
    this._resultCloseRect = null;
    if (!result) return;
    // 1명이면 기존 하단 Winner 표시, 여러명이면 화면 중앙 당첨자 목록 팝업
    if (result.length === 1) {
      this.renderWinner(result[0], params.theme);
    } else if (!this._resultPopupClosed) {
      this.renderWinnerList(result, params);
    }
  }

  /** 결과 팝업 닫기 버튼을 눌렀는지 */
  getResultCloseHitAt(x: number, y: number): boolean {
    return inRect(this._resultCloseRect ?? undefined, x, y);
  }

  closeResultPopup(): void {
    this._resultPopupClosed = true;
  }

  /**
   * 바구니 모드 최종 결과. 칸 하나가 한 열이다.
   * renderResult 다음에 호출되어야 한다. 그쪽이 매 프레임 _resultCloseRect 를 비우기 때문
   */
  /**
   * 팝업이 차지한 아래 끝을 CSS 변수로 알린다. 결과는 캔버스에 그려지고 세팅 패널은 DOM 이라
   * 서로 위치를 모르기 때문에, 이 값으로 세팅 패널을 팝업 밑으로 내린다 (style.scss 의
   * html.result-open #settings). 매 프레임 DOM 을 건드리지 않도록 값이 바뀔 때만 쓴다
   */
  private _publishResultBottom(bottomPx: number | null) {
    const next = bottomPx === null ? -1 : Math.round(bottomPx);
    if (next === this._resultBottomPx) return;
    this._resultBottomPx = next;

    const root = document.documentElement;
    if (next < 0) {
      root.classList.remove('result-open');
      root.style.removeProperty('--result-bottom');
    } else {
      root.style.setProperty('--result-bottom', `${next}px`);
      root.classList.add('result-open');
    }
  }

  private renderBasketResult(params: RenderParameters) {
    const state = params.baskets;
    const finished = !!state?.finished;
    // 라운드가 끝나는 순간 닫힘 상태를 푼다
    if (finished !== this._lastBasketFinished) {
      this._lastBasketFinished = finished;
      this._resultPopupClosed = false;
    }
    if (!finished || !state || this._resultPopupClosed) {
      this._publishResultBottom(null);
      return;
    }

    const ctx = this.ctx;
    const w = this._sceneCanvas.width;
    const h = this._sceneCanvas.height;
    const theme = params.theme;
    const count = state.baskets.length;

    const lineHeight = Math.min(26, Math.max(13, h * 0.04));
    const padding = lineHeight;
    const titleHeight = lineHeight * 2;
    const headerHeight = lineHeight * 1.5;

    const colWidth = Math.min(RESULT_COLUMN_MAX_WIDTH, (w * RESULT_PANEL_MAX_WIDTH_RATIO - padding * 2) / count);
    const longest = state.baskets.reduce((max, basket) => Math.max(max, basket.members.length), 0);

    // 이름을 한 줄에 여러 개 늘어놓아 전부 보여준다. 예전에는 한 줄에 하나씩 세로로만 쌓아서
    // 칸이 모자라면 '+n more' 로 접었다
    const maxMemberLineHeight = lineHeight * RESULT_MEMBER_LINE_SCALE;
    ctx.save();
    // 폭은 가장 커질 수 있는 글자로 재둔다. 실제 글자는 이보다 작아질 수만 있어서 안전하다
    ctx.font = `${maxMemberLineHeight * RESULT_MEMBER_FONT_RATIO}px sans-serif`;
    const widestName = state.baskets.reduce(
      (max, basket) => basket.members.reduce((m, member) => Math.max(m, ctx.measureText(member.name).width), max),
      // 이름이 하나도 없어도 칸 폭이 0이 되지 않게 바닥을 깔아준다
      lineHeight
    );
    ctx.restore();

    const cellGap = lineHeight * 0.5;
    const cellWidth = widestName + cellGap;
    const fitByWidth = Math.max(1, Math.floor(colWidth / cellWidth));
    const membersHeight = h * RESULT_BASKET_MAX_HEIGHT_RATIO - titleHeight - headerHeight - padding * 2;

    let perRow = Math.min(fitByWidth, RESULT_MEMBERS_PER_ROW);
    let memberRows = Math.max(1, Math.ceil(longest / perRow));
    // 최소 줄 높이로도 세로가 모자라면 한 줄에 더 채운다. 접어서 숨기지는 않는다
    while (
      memberRows * RESULT_MEMBER_MIN_LINE_HEIGHT > membersHeight &&
      perRow < Math.min(fitByWidth, RESULT_MEMBERS_PER_ROW_MAX)
    ) {
      perRow++;
      memberRows = Math.max(1, Math.ceil(longest / perRow));
    }
    const memberLineHeight = Math.max(
      RESULT_MEMBER_MIN_LINE_HEIGHT,
      Math.min(maxMemberLineHeight, membersHeight / memberRows)
    );

    const panelW = colWidth * count + padding * 2;
    const panelH = titleHeight + headerHeight + memberRows * memberLineHeight + padding;
    const panelX = (w - panelW) / 2;
    // 세로 중앙에 두면 낮은 창에서 아래쪽 세팅 패널이 결과를 가린다. 위쪽에 붙이고,
    // 팝업 아래 남는 높이를 세팅 패널이 쓰도록 알려준다
    const panelY = Math.max(closeButtonSize(h) / 2 + 2, padding);
    // 캔버스는 position:fixed inset:0 이라 씬 좌표를 sizeFactor 로 나누면 뷰포트 px 가 된다
    this._publishResultBottom((panelY + panelH) / this.sizeFactor);

    ctx.save();

    ctx.fillStyle = theme.winnerBackground;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = theme.background;
    ctx.fillRect(panelX, panelY, panelW, panelH);
    ctx.strokeStyle = theme.winnerText;
    ctx.lineWidth = 2;
    ctx.strokeRect(panelX, panelY, panelW, panelH);

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = theme.winnerText;
    ctx.font = `bold ${lineHeight * 1.1}px sans-serif`;
    ctx.fillText(`팀 선정 결과 (총 ${state.total}명)`, w / 2, panelY + titleHeight / 2);

    this._resultCloseRect = drawCloseCircle(ctx, panelX + panelW, panelY, closeButtonSize(h), '#222');

    const membersTop = panelY + titleHeight + headerHeight;
    // 줄 높이가 줄어들면 글자도 같이 줄여야 줄끼리 붙지 않는다
    const memberFont = `${memberLineHeight * RESULT_MEMBER_FONT_RATIO}px sans-serif`;

    state.baskets.forEach((basket, index) => {
      const colLeft = panelX + padding + colWidth * index;
      const centerX = colLeft + colWidth / 2;
      const hue = RouletteRenderer.basketHue(index, count);

      ctx.save();
      ctx.beginPath();
      ctx.rect(colLeft, panelY + titleHeight, colWidth, panelH - titleHeight);
      ctx.clip();

      // 이름 글자를 키운 만큼 머리글도 올린다. 안 그러면 팀 이름이 그 아래 번호보다 작아진다
      ctx.font = `bold ${lineHeight * 1.05}px sans-serif`;
      ctx.fillStyle = `hsl(${hue} 90% ${theme.marbleLightness}%)`;
      ctx.fillText(`${basket.name} (${basket.members.length})`, centerX, membersTop - headerHeight / 2);

      ctx.font = memberFont;
      basket.members.forEach((member, i) => {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        // 마지막 줄이 덜 찼으면 그 줄만 가운데로 모은다
        const inRow = Math.min(perRow, basket.members.length - row * perRow);
        const rowLeft = centerX - (inRow * cellWidth) / 2;
        ctx.fillStyle = `hsl(${member.hue} 100% ${theme.marbleLightness}%)`;
        ctx.fillText(
          member.name,
          rowLeft + cellWidth * col + cellWidth / 2,
          membersTop + row * memberLineHeight + memberLineHeight / 2
        );
      });
      ctx.restore();
    });

    ctx.restore();
  }

  /**
   * 여러명 모드에서 확정된 당첨자를 좌측 상단에 상시 표시한다.
   * 우측은 랭킹 리스트가 구슬 수만큼 내려오므로 겹친다. 좌측은 미니맵이 세로로 긴
   * 스트립이라 그 오른쪽에 붙인다.
   */
  private renderWinnerProgress({ winners, winnerRange, result, theme }: RenderParameters) {
    const { start, end } = winnerRange;
    if (end <= start) return; // 1명 추첨은 기존 하단 Winner 표시를 쓴다

    const ctx = this.ctx;
    const w = this._sceneCanvas.width;
    const h = this._sceneCanvas.height;

    const lineHeight = Math.min(24, Math.max(14, h * 0.042));
    const pad = lineHeight * 0.6;
    const rankWidth = lineHeight * 1.9;
    const headerFont = `bold ${lineHeight * 0.7}px sans-serif`;
    const rankFont = `${lineHeight * 0.6}px sans-serif`;
    const nameFont = `bold ${lineHeight * 0.72}px sans-serif`;

    // 확정 전에는 골인한 당첨자만, 확정 후에는 최종 명단(조기 확정분 포함)을 쓴다
    const confirmed = result ?? winners.slice(start, end + 1);
    const header = `Winners ${confirmed.length} / ${end - start + 1}`;

    // 화면을 넘기면 오래된 쪽을 접는다. 전체 명단은 어차피 중앙 팝업에서 보여준다
    const maxRows = Math.max(1, Math.floor((h * 0.55) / lineHeight) - 2);
    const hidden = Math.max(0, confirmed.length - maxRows);
    const shown = confirmed.slice(hidden);
    const foldLabel = `+${hidden} more`;

    ctx.save();

    ctx.font = headerFont;
    let contentW = ctx.measureText(header).width;
    ctx.font = nameFont;
    for (const marble of shown) {
      contentW = Math.max(contentW, rankWidth + ctx.measureText(marble.name).width);
    }
    if (hidden > 0) {
      ctx.font = rankFont;
      contentW = Math.max(contentW, ctx.measureText(foldLabel).width);
    }

    const panelW = Math.min(contentW + pad * 2, w * PROGRESS_MAX_WIDTH_RATIO);
    const rows = shown.length + (hidden > 0 ? 1 : 0);
    // 헤더와 목록 사이 간격은 목록이 있을 때만 준다. 항상 주면 당첨자가 없을 때
    // 아래쪽에만 빈 공간이 남아 위아래 여백이 어긋난다
    const headerGap = rows > 0 ? lineHeight * 0.35 : 0;
    const panelH = pad * 2 + lineHeight + headerGap + rows * lineHeight;
    const panelX = MINIMAP_INSET + MINIMAP_WIDTH + pad;
    const panelY = MINIMAP_INSET; // 미니맵 상단과 맞춘다

    ctx.fillStyle = theme.winnerBackground;
    ctx.fillRect(panelX, panelY, panelW, panelH);
    ctx.strokeStyle = PROGRESS_ACCENT;
    ctx.lineWidth = 2;
    ctx.strokeRect(panelX, panelY, panelW, panelH);

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.font = headerFont;
    ctx.fillStyle = theme.winnerText;
    ctx.fillText(header, panelX + pad, panelY + pad + lineHeight / 2);

    let y = panelY + pad + lineHeight + headerGap + lineHeight / 2;
    if (hidden > 0) {
      ctx.font = rankFont;
      ctx.fillStyle = theme.winnerText;
      ctx.fillText(foldLabel, panelX + pad, y);
      y += lineHeight;
    }

    shown.forEach((marble, i) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(panelX, y - lineHeight / 2, panelW, lineHeight);
      ctx.clip();

      ctx.font = rankFont;
      ctx.fillStyle = theme.winnerText;
      ctx.fillText(`#${start + hidden + i + 1}`, panelX + pad, y);

      ctx.font = nameFont;
      ctx.fillStyle = `hsl(${marble.hue} 100% ${theme.marbleLightness}%)`;
      ctx.fillText(marble.name, panelX + pad + rankWidth, y);
      ctx.restore();
      y += lineHeight;
    });

    ctx.restore();
  }

  /** 당첨자가 여러명일 때 화면 중앙에 목록 팝업을 그린다 */
  private renderWinnerList(winners: Marble[], { theme, winnerRange }: RenderParameters) {
    const ctx = this.ctx;
    const w = this._sceneCanvas.width;
    const h = this._sceneCanvas.height;

    const lineHeight = Math.min(32, Math.max(16, h * 0.05));
    const padding = lineHeight;
    const titleHeight = lineHeight * 2;

    // 세로로 다 안 들어가면 열을 늘린다
    const maxRows = Math.max(
      1,
      Math.floor((h * RESULT_PANEL_MAX_HEIGHT_RATIO - titleHeight - padding * 2) / lineHeight)
    );
    const cols = Math.max(1, Math.ceil(winners.length / maxRows));
    const rows = Math.ceil(winners.length / cols);

    const colWidth = Math.min(RESULT_COLUMN_MAX_WIDTH, (w * RESULT_PANEL_MAX_WIDTH_RATIO - padding * 2) / cols);
    const panelW = colWidth * cols + padding * 2;
    const panelH = titleHeight + rows * lineHeight + padding;
    const panelX = (w - panelW) / 2;
    const panelY = (h - panelH) / 2;

    ctx.save();

    ctx.fillStyle = theme.winnerBackground;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = theme.background;
    ctx.fillRect(panelX, panelY, panelW, panelH);
    ctx.strokeStyle = theme.winnerText;
    ctx.lineWidth = 2;
    ctx.strokeRect(panelX, panelY, panelW, panelH);

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = theme.winnerText;
    ctx.font = `bold ${lineHeight * 1.1}px sans-serif`;
    ctx.fillText(`Winners (${winners.length})`, w / 2, panelY + titleHeight / 2);

    // 버튼 중심을 팝업 우상단 꼭지점에 맞춰 걸쳐놓는다. 뒤가 비치지 않게 불투명하게 채우되,
    // 검정으로 채우면 다크 테마에서 배경과 같아져 버튼으로 안 보이므로 대비되는 색을 쓴다
    this._resultCloseRect = drawCloseCircle(ctx, panelX + panelW, panelY, closeButtonSize(h), '#222');

    const rankWidth = lineHeight * 1.8;
    winners.forEach((marble, i) => {
      const col = Math.floor(i / rows);
      const row = i % rows;
      const x = panelX + padding + col * colWidth;
      const y = panelY + titleHeight + row * lineHeight + lineHeight / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y - lineHeight / 2, colWidth, lineHeight);
      ctx.clip();

      ctx.textAlign = 'right';
      ctx.fillStyle = theme.winnerText;
      ctx.font = `${lineHeight * 0.6}px sans-serif`;
      ctx.fillText(`#${winnerRange.start + i + 1}`, x + rankWidth * 0.8, y);

      ctx.textAlign = 'left';
      ctx.fillStyle = `hsl(${marble.hue} 100% ${theme.marbleLightness}%)`;
      ctx.font = `bold ${lineHeight * 0.75}px sans-serif`;
      ctx.fillText(marble.name, x + rankWidth, y);
      ctx.restore();
    });

    ctx.restore();
  }

  private renderWinner(winner: Marble, theme: ColorTheme) {
    this.ctx.save();
    this.ctx.fillStyle = theme.winnerBackground;
    this.ctx.fillRect(
      this._sceneCanvas.width / 2,
      this._sceneCanvas.height - winnerAreaHeight,
      this._sceneCanvas.width / 2,
      winnerAreaHeight
    );

    // Draw marble image or colored circle
    const marbleSize = 100;
    const marbleCenterX = this._sceneCanvas.width - marbleSize / 2 - 20;
    const marbleCenterY = this._sceneCanvas.height - winnerAreaHeight / 2;
    const marbleImage = this.getMarbleImage(winner.name);

    if (marbleImage) {
      this.ctx.drawImage(
        marbleImage,
        marbleCenterX - marbleSize / 2,
        marbleCenterY - marbleSize / 2,
        marbleSize,
        marbleSize
      );
    } else {
      // 구슬과 같은 열매를 보여준다. 스프라이트가 본체 밖 여유분을 포함하므로 그만큼 크게 그린다
      const half = (marbleSize / 2) * SPRITE_EXTENT;
      this.ctx.drawImage(getFruitSprite(winner.fruit), marbleCenterX - half, marbleCenterY - half, half * 2, half * 2);
    }

    this.ctx.fillStyle = theme.winnerText;
    this.ctx.strokeStyle = theme.winnerOutline;

    this.ctx.font = 'bold 48px sans-serif';
    this.ctx.textAlign = 'right';
    this.ctx.lineWidth = 4;
    const textRightX = marbleCenterX - marbleSize / 2 - 20;
    if (theme.winnerOutline) {
      this.ctx.strokeText('Winner', textRightX, this._sceneCanvas.height - 120 + WINNER_TEXT_OFFSET);
    }

    this.ctx.fillText('Winner', textRightX, this._sceneCanvas.height - 120 + WINNER_TEXT_OFFSET);
    this.ctx.font = 'bold 72px sans-serif';
    this.ctx.fillStyle = `hsl(${winner.hue} 100% ${theme.marbleLightness})`;
    if (theme.winnerOutline) {
      this.ctx.strokeText(winner.name, textRightX, this._sceneCanvas.height - 55 + WINNER_TEXT_OFFSET);
    }
    this.ctx.fillText(winner.name, textRightX, this._sceneCanvas.height - 55 + WINNER_TEXT_OFFSET);
    this.ctx.restore();
  }
}
